import { useChatContext } from '@/context/ChatProvider'
import { chatResetOnNewSession, shouldResetChatForNewSession } from '@/lib/session-auth-state'
import { useChatStore } from '@/store/chat.store'
import { usePersistStore } from '@/store/persist.store'
import { useSocketStore } from '@/store/socket.store'
import { useTranslations } from 'next-intl'

export const useAuthHandlers = () => {
  const t = useTranslations('chat')
  const setUser = usePersistStore(state => state.setUser)
  const setSessionId = usePersistStore(state => state.setSessionId)
  const setIsGuestIdActive = usePersistStore(state => state.setIsGuestIdActive)
  const setIsTakenOver = usePersistStore(state => state.setIsTakenOver)
  const token = usePersistStore(state => state.token)
  const setIsGuestInfoRequired = useChatStore(state => state.setIsGuestInfoRequired)
  const setMessages = useChatStore(state => state.setMessages)
  const setIsChatEnded = useChatStore(state => state.setIsChatEnded)
  const setLiveSupportState = useChatStore(state => state.setLiveSupportState)
  const setOperatorName = useChatStore(state => state.setOperatorName)
  const setPendingReview = useChatStore(state => state.setPendingReview)
  const setSelectedSection = useChatStore(state => state.setSelectedSection)
  const setIsShowSectionSelectButtons = useChatStore(state => state.setIsShowSectionSelectButtons)

  const setIsAuthSuccess = useSocketStore(state => state.setIsAuthSuccess)
  const setIsReconnecting = useSocketStore(state => state.setIsReconnecting)
  const setRetryAttempt = useSocketStore(state => state.setRetryAttempt)
  const setIsPreparing = useSocketStore(state => state.setIsPreparing)

  const { scrollToBottom } = useChatContext()

  const handleAuthSuccess = (data: any) => {
    setUser({ userId: data?.userId, userName: data?.userName })

    const sessionIdValue = data?.sessionId
    const previousSessionId = usePersistStore.getState().sessionId
    const willReset = shouldResetChatForNewSession(previousSessionId, sessionIdValue)

    if (willReset) {
      const reset = chatResetOnNewSession()
      setMessages(reset.messages)
      setIsChatEnded(reset.isChatEnded)
      setLiveSupportState(reset.liveSupportState)
      setOperatorName(reset.operatorName)
      setPendingReview(null)
      setSelectedSection(reset.selectedSection)
      setIsShowSectionSelectButtons(reset.isShowSectionSelectButtons)
      setIsTakenOver(reset.isTakenOver)
    }

    if (sessionIdValue) {
      setSessionId(sessionIdValue)
    }

    if (token !== 'guest') {
      setIsGuestIdActive(false)
    }

    setIsGuestInfoRequired(false)
    setIsAuthSuccess(true)
    setIsReconnecting(false)
    setRetryAttempt(0)
    setIsPreparing(false)
    scrollToBottom()
  }

  const handleAuthError = (data: any, addErrorMessage: (message: string) => void) => {
    setIsAuthSuccess(false)
    setIsPreparing(false)
    const setSocketConnectionStatus = useSocketStore.getState().setSocketConnectionStatus
    setSocketConnectionStatus('idle')
    setIsReconnecting(false)
    addErrorMessage(data.message || t('authErrorTitle'))
    scrollToBottom()
  }

  const handleAuthFailed = (data: any, addErrorMessage: (message: string) => void) => {
    setIsAuthSuccess(false)
    setIsPreparing(false)
    const setSocketConnectionStatus = useSocketStore.getState().setSocketConnectionStatus
    setSocketConnectionStatus('idle')
    setIsReconnecting(false)
    addErrorMessage(data.message || t('authErrorTitle'))
    scrollToBottom()
  }

  return {
    handleAuthSuccess,
    handleAuthError,
    handleAuthFailed
  }
}
